import { useState } from "react";
import { Plus } from "lucide-react";
import SectionHeading from "../Plan2Benefit/SectionHeading.jsx";
import Reveal from "../Plan2Benefit/Reveal.jsx";
import WithStar from "./WithStar.jsx";
import { faqs, faqDisclaimer } from "./data.js";

/* ─────────────────────────────────────────────────────────────
   FAQ — the last section before the (globally-rendered) Footer.
   Ten questions covering how the Car Plan works end to end, most
   importantly the "does the app hold my money" question (answer:
   no — it's placed with Suryoday Small Finance Bank) addressed
   head-on rather than left implicit.

   Single-open accordion: one answer expanded at a time, using a
   CSS grid-template-rows trick (0fr → 1fr) to animate to "auto"
   height with a plain transition instead of measuring pixels in
   JS or pulling in framer-motion for something this simple.
───────────────────────────────────────────────────────────── */
export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="faq" className="bg-white px-4 py-16 sm:px-6 lg:px-8 lg:py-32">
      <div className="mx-auto max-w-3xl">
        <Reveal className="text-center">
          <SectionHeading
            eyebrow="Good to know"
            title="Frequently asked"
            highlight="questions"
            centered
          />
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-12 overflow-hidden rounded-[26px] border border-[#eceafb] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03),0_24px_60px_-30px_rgba(0,0,0,0.09)] sm:mt-14">
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              const panelId = `faq-panel-${index}`;
              const buttonId = `faq-button-${index}`;
              return (
                <div
                  key={faq.question}
                  className="border-b border-[#f0eefb] bg-white last:border-b-0"
                >
                  <h3 className="m-0">
                    <button
                      id={buttonId}
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpenIndex(isOpen ? -1 : index)}
                      className="flex w-full items-center justify-between gap-4 px-6 py-6 text-left sm:px-8"
                    >
                      <span className="font-montserrat text-[15px] font-bold tracking-[-0.015em] text-[#0d0828] sm:text-[17px]">
                        {faq.question}
                      </span>
                      <span
                        className={`flex h-8 w-8 flex-none items-center justify-center rounded-full border transition duration-300 ${
                          isOpen
                            ? "rotate-45 border-transparent bg-[#4328E7] text-white"
                            : "border-[#e6e3f7] bg-white text-[#4328E7]"
                        }`}
                      >
                        <Plus className="h-4 w-4" strokeWidth={2.5} />
                      </span>
                    </button>
                  </h3>

                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    className={`grid transition-all duration-300 ${
                      isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-6 sm:px-8">
                        <p className="font-montserrat text-sm leading-7 text-[#0d0828b3] sm:text-base">
                          <WithStar text={faq.answer} />
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mx-auto mt-6 max-w-2xl text-center font-montserrat text-[12px] leading-5 text-[#0d0828]/40">
            {faqDisclaimer}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
